import React, { useMemo, useState } from 'react'

import { mediaQueries } from '@centivo/react-components'
import { Switch, Route, Redirect, RouteComponentProps } from 'react-router-dom'
import { useMediaLayout } from 'use-media'
import { parse } from 'query-string'
import { noop } from 'lodash'

import { TemplateStyles } from 'src/refactor/structure/Templates'
import { MediaContext } from 'src/context/responsive'
import { useToggle, useWatcher, useDidMount } from 'src/hooks'
import { Header } from 'src/layout/Header'
import { Contact } from 'src/refactor/blocks/Contact'
import { useGlobalState } from 'src/context/global'

import { Home } from './home'
import { PractitionerDetails } from './practitionerDetails'
import { Results } from './results'
import { LoadingScreen, PrivacyBanner, HiddenAriaField } from '../components'

export const Routes: React.FC<RouteComponentProps> = ({
  history,
  location,
}) => {
  const [isModalOpen, toggleModal] = useToggle(false)
  const [isBannerOpen, setBannerOpen] = useState(true)
  const [isKeyboardOpen, setKeyboardOpen] = useState(false)
  const [{ appConfig, isLoading }] = useGlobalState()

  const {
    shouldShowHeader,
    shouldShowContactForm,
    customStyle,
    template,
    pageTitle,
  } = appConfig

  const isMobileView = useMediaLayout(mediaQueries.mobile, true)

  const value = useMemo(
    () =>
      isMobileView
        ? {
            isMobileView,
            isKeyboardOpen,
          }
        : {
            isMobileView,
          },
    [isMobileView, isKeyboardOpen]
  )

  useWatcher(
    () => {
      window.scrollTo(0, 0)
    },
    { pathname: location.pathname }
  )

  useDidMount(() => {
    if (customStyle) {
      const linkTag = document.createElement('link')
      linkTag.setAttribute('rel', 'stylesheet')
      linkTag.setAttribute('href', customStyle)

      document.head.insertAdjacentElement('beforeend', linkTag)
    }

    if (pageTitle) document.title = pageTitle
  })

  useDidMount(() => {
    const { search } = location
    const { contactSubmitted, contactFailed, from } = parse(search)

    if (contactSubmitted === 'true' || contactFailed === 'true') {
      switch (from) {
        case 'home':
          history.push({ pathname: '/', hash: 'contact', search })
          break
        case 'contact':
          history.push({
            pathname: '/contact',
            state: {
              from: '/',
            },
            search,
          })
          break
        default:
          toggleModal()
      }
    }

    const isMobile = window.innerWidth <= 767
    if (isMobile) {
      const height = window.innerHeight
      const onResize: EventListener = e => {
        setKeyboardOpen((e.currentTarget as VisualViewport).height < height)
      }

      window.visualViewport?.addEventListener('resize', onResize)
      return () =>
        window.visualViewport?.removeEventListener('resize', onResize)
    }

    return noop
  })

  const onRequestBannerClose = () => {
    setBannerOpen(false)
  }

  const onRequestContact = () => {
    const contactSection = document.querySelector('#contact')

    if (isMobileView || !contactSection) {
      toggleModal()

      return
    }

    window.scrollTo({
      top: contactSection.getBoundingClientRect().top - 50,
      behavior: 'smooth',
    })
  }

  if (isLoading) return <LoadingScreen />

  return (
    <MediaContext.Provider value={value}>
      <TemplateStyles />
      <PrivacyBanner
        isOpen={isBannerOpen}
        privacyPolicyUrl="https://s3.amazonaws.com/centivo-provider-employer-resources/600_Centivo_Privacy_Policy.pdf"
        onRequestClose={onRequestBannerClose}
      />
      {shouldShowHeader && (
        <Header
          className={`${template}--header`}
          onRequestContact={onRequestContact}
        />
      )}
      <Contact.Modal isOpen={isModalOpen} onRequestClose={toggleModal} />
      <Switch>
        <Route
          exact
          path="/"
          render={props => (
            <>
              <HiddenAriaField text="Home page" />
              <Home {...props} isBannerOpen={isBannerOpen} />
            </>
          )}
        />
        <Route
          exact
          path="/results"
          render={props => (
            <>
              <HiddenAriaField text="Centivo Provider Search - Results page" />
              <Results {...props} />
            </>
          )}
        />
        <Route exact path="/doctorDetails">
          <HiddenAriaField text="Centivo Provider Search - Doctor details page" />
          <PractitionerDetails />
        </Route>
        {isMobileView && shouldShowContactForm && (
          <Route exact path="/contact">
            <HiddenAriaField text="Contact" />
            <Contact />
          </Route>
        )}
        <Route render={() => <Redirect to="/" />} />
      </Switch>
    </MediaContext.Provider>
  )
}
